
import * as Sentry from "@sentry/browser";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";

import { ViewAnalytics } from "@/mixins";
import { ApiService, LogService, RouterService } from "@/services";
import { ApplicationModule, getStoreModule, SessionModule } from "@/store";
import { VignetteRoutes } from "@/types";

@Component
export default class Loading extends mixins(ViewAnalytics) {
  private applicationStore!: ApplicationModule;
  private sessionStore!: SessionModule;

  // Override for PageTitle mixin
  pageName = "Loading";

  created(): void {
    this.applicationStore = getStoreModule(ApplicationModule, this.$store);
    this.sessionStore = getStoreModule(SessionModule, this.$store);
  }

  mounted(): void {
    ApiService.getAppConfig().then(() => {
      ApiService.getSession().then(() => {
        if (this.sessionStore.hasVignetteAccess) {
          ApiService.refreshBriefcase().then(() => {
            this.loadComplete();
          });
        } else {
          this.loadComplete();
        }
      });
    });
  }

  loadComplete(): void {
    LogService.debug("Vue app finished loading all dependencies.");
    this.applicationStore.setLoading(false);

    // Customize Sentry with user info, if available
    this.customizeSentry();

    const redirectPath = this.applicationStore.getRedirectPath;

    // Clear redirect path
    this.applicationStore.setRedirectPath("");

    /** IMPORTANT NOTE: redirectPath never empty on app load, it's always set to "/" */

    // "!redirectPath" returns true if falsy, it is JS/TS shorthand to check falsy values (null,undefined,"",0)
    if (this.sessionStore.hasLapsedSubscription) {
      // Only for lapsed subscriptions, automatically redirect from Welcome to My Account page after login / app load.
      if (!redirectPath || redirectPath === "/") {
        RouterService.navigateByName(this.$router, VignetteRoutes.MyAccount);
      } else {
        RouterService.navigateByPath(this.$router, redirectPath);
      }
    } else {
      if (!redirectPath) {
        RouterService.navigateToHome(this.$router);
      } else {
        RouterService.navigateByPath(this.$router, redirectPath);
      }
    }
  }

  // Customize Sentry with user info, if available
  customizeSentry(): void {
    if (LogService.isSentryLoaded) {
      const isLoggedIn = this.sessionStore.isLoggedIn;
      const hasVignetteAccess = this.sessionStore.hasVignetteAccess;
      const user = this.sessionStore.getUser;

      Sentry.configureScope(function (scope) {
        if (user) {
          scope.setUser({
            email: user.email,
            username: user.username,
          });
          scope.setTag("isTrial", String(user.isVignetteTrialActive) || "");
          scope.setTag("userType", user.userType);
        }
        scope.setTag("isLoggedIn", String(isLoggedIn));
        scope.setTag("hasVignetteAccess", String(hasVignetteAccess));
      });
    }
  }
}
